import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "../api";
import type { Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS } from "./intervals";

export type Template = Datified<Schemas["Template"], "created_at">;
export type TemplateVersion = Datified<Schemas["TemplateVersion"], "published_at">;
export type TemplateDetail = Template &
  Omit<Schemas["TemplateDetail"], keyof Schemas["Template"] | "draft" | "current"> & {
    draft?: TemplateVersion;
    current?: TemplateVersion;
  };
export type TemplateCreate = Schemas["TemplateCreate"];
export type TemplateKind = Schemas["Template"]["kind"];
export type TemplateDraftUpdate = Schemas["TemplateDraftUpdate"];
export type DraftPreviewRequest = Schemas["DraftPreviewRequest"];
export type PreviewResponse = Schemas["PreviewResponse"];
export type TemplatePartial = Schemas["TemplatePartial"];
export type TemplateTestSendInput = {
  sender: string;
  to: string;
  data?: unknown;
};
export type EnqueueResponse = Schemas["EnqueueResponse"];

export function useTemplateList() {
  return useQuery<Template[]>({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await apiGet<{ items: Template[] }>("/templates");
      return res.items;
    },
    refetchInterval: POLL_BASE_MS,
  });
}

export function useTemplateDetail(id: string | undefined) {
  return useQuery<TemplateDetail>({
    queryKey: ["templates", "detail", id],
    queryFn: () => apiGet<TemplateDetail>(`/templates/${id}`),
    enabled: !!id,
  });
}

export function useTemplateVersions(id: string | undefined) {
  return useQuery<TemplateVersion[]>({
    queryKey: ["templates", "versions", id],
    queryFn: async () => {
      const res = await apiGet<{ items: TemplateVersion[] }>(
        `/templates/${id}/versions`,
      );
      return res.items;
    },
    enabled: !!id,
    refetchInterval: POLL_BASE_MS,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation<TemplateDetail, unknown, TemplateCreate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<TemplateDetail>("/templates", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["templates"] }),
  });
}

export type TemplateUpdate = Schemas["TemplateUpdate"];

/**
 * PATCH /api/templates/{id} — the display name and description. The key never
 * changes: API callers, client pickers and message history reference it.
 */
export function useUpdateTemplate(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation<TemplateDetail, unknown, TemplateUpdate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPatch<TemplateDetail>(`/templates/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates", "detail", id] });
      void qc.invalidateQueries({ queryKey: ["templates"], exact: true });
    },
  });
}

/**
 * PUT /api/templates/{id}/draft — saves the working draft. When the latest
 * version is already published the server silently forks a new draft version;
 * the returned `TemplateVersion` carries the (possibly new) version number.
 */
export function useUpdateTemplateDraft(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation<TemplateVersion, unknown, TemplateDraftUpdate>({
    mutationFn: (input) => apiPut<TemplateVersion>(`/templates/${id}/draft`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates", "detail", id] });
      void qc.invalidateQueries({ queryKey: ["templates", "versions", id] });
      void qc.invalidateQueries({ queryKey: ["templates"], exact: true });
    },
  });
}

/** POST /api/templates/{id}/publish — 409 `template_no_draft` when no draft. */
export function usePublishTemplate(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation<TemplateVersion, unknown, void>({
    meta: { suppressErrorToast: true },
    mutationFn: () => apiPost<TemplateVersion>(`/templates/${id}/publish`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["templates", "detail", id] });
      void qc.invalidateQueries({ queryKey: ["templates", "versions", id] });
      void qc.invalidateQueries({ queryKey: ["templates"], exact: true });
    },
  });
}

/**
 * POST /api/templates/preview — renders arbitrary (unsaved) draft content.
 * Fired from the debounced live preview; 422 carries a template error
 * that the preview pane renders inline, hence no error toast.
 */
export function usePreviewTemplateDraft() {
  return useMutation<PreviewResponse, unknown, DraftPreviewRequest>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<PreviewResponse>("/templates/preview", input),
  });
}

/** POST /api/templates/{id}/test-send — enqueues a test message (201). */
export function useTemplateTestSend(id: string | undefined) {
  return useMutation<EnqueueResponse, unknown, TemplateTestSendInput>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<EnqueueResponse>(`/templates/${id}/test-send`, input),
  });
}

export function useTemplatePartials() {
  return useQuery<TemplatePartial[]>({
    queryKey: ["template-partials"],
    queryFn: async () => {
      const res = await apiGet<{ items: TemplatePartial[] }>("/template-partials");
      return res.items;
    },
    refetchInterval: POLL_BASE_MS,
  });
}

export function useUpsertTemplatePartial() {
  const qc = useQueryClient();
  return useMutation<TemplatePartial, unknown, { key: string; html_body: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ key, html_body }) =>
      apiPut<TemplatePartial>(`/template-partials/${encodeURIComponent(key)}`, {
        html_body,
      }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["template-partials"] }),
  });
}

export function useDeleteTemplatePartial() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { key: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ key }) =>
      apiDelete<void>(`/template-partials/${encodeURIComponent(key)}`),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["template-partials"] }),
  });
}
